/* eslint-disable no-alert */
import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import "react-toastify/dist/ReactToastify.css";
import { connect } from "react-redux";
import * as PropTypes from "prop-types";
import moment from "moment";
import SelectorModal from "../common/SelectorModal";
import AttendanceReportForm from "./AttendanceReportForm";
import { STATUS, ROLES } from "../common/Const";
import { DEFAULT_LESSON_TYPES_SETTING, getLessonTypesSetting, isGroupReportLessonType } from "../common/lessonTypes";
import { buildSchoolYearRange } from "../common/schoolYear";
import SettingsService from "../../services/SettingsService";
import { history } from "../../redux/store/configureStore";
import { mandatory, validateValues } from "../common/Validations";
import {
  formatDayOfWeek,
  getPersonName,
  getClassType,
  getStudentsByClientId,
  hasErrors,
  hasNoErrors,
} from "../common/Helpers";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as clientActions from "../../redux/actions/clientActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import AttendanceList from "./AttendanceList";

const validationConfig = {
  startDate: {
    label: "data startowa",
    validate: [mandatory],
  },
  endDate: {
    label: "data końcowa",
    validate: [mandatory],
  },
};

const getLessonRegisterClassType = (lessonRegisterItem, schoolClasses) => (
  (lessonRegisterItem.lessonDetails && (
    lessonRegisterItem.lessonDetails.classTypeId ||
    lessonRegisterItem.lessonDetails.classType
  )) ||
  getClassType(lessonRegisterItem.lessonId, schoolClasses)
);

const validateDateOrder = (startDate, endDate, fullMonthChange = false) => {
  if (endDate && startDate && endDate <= startDate) {
    if (!fullMonthChange) {
      return {start: endDate, end: endDate, repeat: true};
    }
    return {
      start: moment(endDate).startOf("month").toDate(),
      end: endDate,
      repeat: true,
    };
  }
  return { start: startDate, end: endDate, repeat: false };
};

const clampDateToSchoolYearRange = (date, schoolYearRange) => {
  if (!date) return date;

  const startBoundary = moment(`${schoolYearRange.fromDate}T00:00:00`);
  const endBoundary = moment(`${schoolYearRange.tillDate}T00:00:00`);
  const momentDate = moment(date);

  if (momentDate.isBefore(startBoundary, "day")) {
    return startBoundary.toDate();
  }

  if (momentDate.isAfter(endBoundary, "day")) {
    return endBoundary.toDate();
  }

  return momentDate.toDate();
};

const clampCalendarDateToSchoolYearRange = (date, schoolYearRange) => {
  const clampedDate = clampDateToSchoolYearRange(date, schoolYearRange);
  const monthStart = moment(clampedDate).startOf("month");
  const rangeStart = moment(`${schoolYearRange.fromDate}T00:00:00`).startOf("month");
  const rangeEnd = moment(`${schoolYearRange.tillDate}T00:00:00`).startOf("month");

  if (monthStart.isBefore(rangeStart, "month")) {
    return moment(`${schoolYearRange.fromDate}T00:00:00`).toDate();
  }

  if (monthStart.isAfter(rangeEnd, "month")) {
    return moment(`${schoolYearRange.tillDate}T00:00:00`).toDate();
  }

  return monthStart.toDate();
};

export function AttendanceReport({
  userId,
  lessonRegister,
  loadLessonRegisters,
  schoolClasses,
  loadSchoolClasses,
  teachers,
  loadTeachers,
  students,
  loadStudents,
  classStudents,
  loadClassStudents,
  clients,
  loadClients,
  clientOptions,
  teacherOptions,
  activeSchoolYearStartYear,
  userRole,
}) {
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const canReadClientRecords = userRole !== ROLES.TEACHER;
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(STATUS.VIEW);
  const [showSelectTeacherModal, setShowSelectTeacherModal] = useState(false);
  const [showSelectClientModal, setShowSelectClientModal] = useState(false);
  const [report, setReport] = useState({
    startDate: new Date(`${activeSchoolYearRange.fromDate}T00:00:00`),
    endDate: new Date(`${activeSchoolYearRange.tillDate}T00:00:00`),
    teacherId: null,
    teacherName: "",
    clientId: null,
    clientName: "",
  });
  const [attendance, setAttendance] = useState([]);
  const [selectionState, setSelectionState] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [firstCalendarDate, setFirstCalendarDate] = useState(report.startDate);
  const [secondCalendarDate, setSecondCalendarDate] = useState(report.endDate);
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  const location = useLocation();
  const yearOptions = [
    { id: activeSchoolYearStartYear, name: activeSchoolYearStartYear.toString() },
    { id: activeSchoolYearStartYear + 1, name: String(activeSchoolYearStartYear + 1) },
  ];
  const monthOptions = (yearValue) => {
    const monthList = moment.months().map((name, index) => ({ id: index, name }));
    if (Number(yearValue) === activeSchoolYearStartYear) {
      return monthList.filter(month => month.id >= 8);
    }

    return monthList.filter(month => month.id <= 7);
  };
  
  const handleSelectionState = (value) => {
    setSelectionState(value);
  };
  
  const handleNavigateFirstCalendar = (date) => {
    const newFirstCalendarDate = moment(clampCalendarDateToSchoolYearRange(date, activeSchoolYearRange));
    if (moment(newFirstCalendarDate).isBefore(secondCalendarDate)) {
      setFirstCalendarDate(newFirstCalendarDate.toDate());
    } else {
      setFirstCalendarDate(
        moment(secondCalendarDate).subtract(1, "month").toDate()
      );
    }
  };
  
  
  const handleNavigateSecondCalendar = (date) => {
    const updatedDate = moment(clampCalendarDateToSchoolYearRange(date, activeSchoolYearRange));
    if (updatedDate.isBefore(moment(firstCalendarDate).add(1, "month"))) {
      setSecondCalendarDate(updatedDate.toDate());
      setFirstCalendarDate(moment(updatedDate).subtract(1, "month").toDate());
    } else {
      setSecondCalendarDate(updatedDate.toDate());
    }
  };
  
  useEffect(() => {
    setReport(prevReport => ({
      ...prevReport,
      startDate: new Date(`${activeSchoolYearRange.fromDate}T00:00:00`),
      endDate: new Date(`${activeSchoolYearRange.tillDate}T00:00:00`),
    }));
    setFirstCalendarDate(new Date(`${activeSchoolYearRange.fromDate}T00:00:00`));
    setSecondCalendarDate(new Date(`${activeSchoolYearRange.tillDate}T00:00:00`));
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate]);

  // Load data functions
  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(error => alert(`Loading schoolClasses failed ${error}`));
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(error => alert(`Loading classStudents failed ${error}`));
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(error => alert(`Loading lessonRegisters failed ${error}`));
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    loadClassStudents,
    loadLessonRegisters,
    loadSchoolClasses,
  ]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch(error => alert(`Loading teachers failed ${error}`));
    }
    if (students.length === 0) {
      loadStudents().catch(error => alert(`Loading students failed ${error}`));
    }
    if (canReadClientRecords && clients.length === 0) {
      loadClients().catch(error => alert(`Loading clients failed ${error}`));
    }
  }, [
    canReadClientRecords,
    clients.length,
    loadClients,
    loadStudents,
    loadTeachers,
    students.length,
    teachers.length,
  ]);

  useEffect(() => {
    SettingsService.getSettings()
      .then(settings => setLessonTypes(getLessonTypesSetting(settings)))
      .catch(() => {});
  }, []);


  const validate = () => {
    const { teacherId, clientId } = report;
    const valErrors = validateValues(report, validationConfig);
    if (!teacherId && !clientId) {
      valErrors.tillDate = "Należy wybrać nauczyciela lub klienta";
    }
    setErrors(valErrors);
    return hasNoErrors(valErrors);
  };

  const handleCancel = () => history.goBack();

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    
    setReport((prevReport) => {
      let nextYear = moment(prevReport.startDate).year();
      let nextMonth = moment(prevReport.startDate).month();

      if (name === "year") nextYear = Number(value);
      if (name === "month") nextMonth = Number(value);

      const validMonths = monthOptions(nextYear).map(month => month.id);
      if (!validMonths.includes(nextMonth)) {
        nextMonth = validMonths[0];
      }

      const startDate = moment.utc({ year: nextYear, month: nextMonth, day: 1 }).startOf("month").toDate();
      const endDate = moment.utc(startDate).endOf("month").toDate();

      setFirstCalendarDate(startDate);
      setSecondCalendarDate(moment.utc(startDate).add(1, "month").toDate());
      return ({...prevReport, [name]: value.toString(), startDate, endDate});
    });
    validate();
  };

  const calculateAttendance = () => {
    let studentsOfClient = getStudentsByClientId(students, report.clientId);
    studentsOfClient = studentsOfClient.map(s => s.id);
    const { teacherId } = report;

    const attendanceGroups = lessonRegister
      .filter(
        lr => new Date(lr.lessonDetails.date) >= report.startDate &&
          new Date(lr.lessonDetails.date) <= report.endDate
      )
      .filter(
        lr => teacherId === "all" ||
          lr.lessonDetails.teacherId === teacherId ||
          teacherId === null
      )
      .flatMap(lr => lr.students
        .filter(
          s => isGroupReportLessonType(getLessonRegisterClassType(lr, schoolClasses), lessonTypes) ||
              studentsOfClient.includes(s.id) ||
              report.clientId === null
        )
        .map((s) => {
          const classType = getLessonRegisterClassType(lr, schoolClasses);
          const studentInfo = students.find(student => student.id === s.id);
          const clientId = studentInfo ? studentInfo.clientId : null;

          const client = clients.find(c => c.id === clientId);
          const clientName = client
            ? `${client.firstName} ${client.lastName}`
            : (studentInfo && studentInfo.clientDisplayName) || "Klient nieznany";

          return {
            ...lr,
            student: {
              ...s,
              clientId,
              clientName,
              included: isGroupReportLessonType(classType, lessonTypes)
                ? studentsOfClient.includes(s.id)
                : true,
            },
          };
        }))
      .reduce((acc, lr) => {
        let group = acc[lr.id];
        if (!group) {
          group = {
            id: lr.id,
            date: lr.lessonDetails.date,
            dayOfWeek: formatDayOfWeek(new Date(lr.lessonDetails.date)),
            startTime: lr.lessonDetails.startTime,
            classType: getLessonRegisterClassType(lr, schoolClasses),
            teacherName: getPersonName(lr.lessonDetails.teacherId, teachers),
            presentCount: 0,
            maxMembers: 1,
            students: [],
          };
        }

        const studentWithNormalizedId = {
          ...lr.student,
          id: `${lr.id}-${group.students.length + 1}`,
          studentName: getPersonName(lr.student.id, students),
          isPresent: lr.student.isPresent,
          comment: lr.lessonDetails.comment,
          included: lr.student.included,
        };

        group.students.push(studentWithNormalizedId);

        if (lr.student.isPresent) {
          group.presentCount += 1;
        }
        group.maxMembers = group.students.length;

        return {
          ...acc,
          [lr.id]: group,
        };
      }, {});

    const newAttendance = Object.values(attendanceGroups);
    setAttendance(newAttendance);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    const isValid = validate();

    if (isValid) {
      calculateAttendance();
      setStatus(STATUS.COMPLETED);
    } else {
      setStatus(STATUS.SUBMITTED);
    }
  };

  const handleSelectTeacher = (teacher) => {
    setReport(prevReport => ({
      ...prevReport,
      teacherId: teacher.value,
      teacherName: teacher.text,
      clientId: prevReport.clientId ? prevReport.clientId : "all",
      clientName: prevReport.clientName ? prevReport.clientName : "Wszyscy",
    }));
    setShowSelectTeacherModal(false);
  };

  const handleSelectClient = (client) => {
    setReport(prevReport => ({
      ...prevReport,
      clientId: client.value,
      clientName: client.text,
      teacherId: prevReport.teacherId ? prevReport.teacherId : "all",
      teacherName: prevReport.teacherName ? prevReport.teacherName : "Wszyscy",
    }));
    setShowSelectClientModal(false);
  };

  // select users as teacher by default if user email matches teachers
  useEffect(() => {
    const teacher = teachers.find(t => t.email === userId);
    if (teacher) {
      handleSelectTeacher({value: teacher.id, text: `${teacher.firstName} ${teacher.lastName}`});
    }
  }, [userId, teachers]);


  const handleChangeReport = () => {
    setStatus(STATUS.VIEW);
  };

  // reset view when location changes
  useEffect(() => {
    handleChangeReport();
  }, [location]);


  const handleDateSelection = (start = null, end = null) => {
    const nextStartDate = clampDateToSchoolYearRange(start || report.startDate, activeSchoolYearRange);
    const nextEndDate = clampDateToSchoolYearRange(end || report.endDate, activeSchoolYearRange);

    setReport(prevReport => ({
      ...prevReport,
      startDate: nextStartDate,
      endDate: nextEndDate,
    }));
  };

  return (
    <div className="mb-5">
      {hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => (
            <div key={key}>{errors[key]}</div>
          ))}
        </div>
      )}
      {(status === STATUS.VIEW || status === STATUS.SUBMITTED) && (
        <AttendanceReportForm
          report={report}
          onDateChange={handleDateChange}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onSelectClient={() => setShowSelectClientModal(true)}
          onSelectTeacher={() => setShowSelectTeacherModal(true)}
          onDateSelection={handleDateSelection}
          onNavigateFirstCalendar={handleNavigateFirstCalendar}
          onNavigateSecondCalendar={handleNavigateSecondCalendar}
          firstCalendarDate={firstCalendarDate}
          secondCalendarDate={secondCalendarDate}
          validateDateOrder={validateDateOrder}
          handleSelectionState={handleSelectionState}
          selectionState={selectionState}
          toggleShowCalendar={() => setShowCalendar(!showCalendar)}
          showCalendar={showCalendar}
          yearOptions={yearOptions}
          monthOptions={monthOptions(moment(report.startDate).year())}
        />
      )}
      {status === STATUS.COMPLETED && (
        <AttendanceList
          userId={userId}
          report={report}
          attendance={attendance}
          onEdit={handleChangeReport}
          lessonTypes={lessonTypes}
        />
      )}

      <SelectorModal
        title="Wybierz nauczyciela"
        isOpen={showSelectTeacherModal}
        onClose={() => setShowSelectTeacherModal(false)}
        values={teacherOptions}
        onSelect={option => handleSelectTeacher(option)}
        selectedIDs={[report.teacherId]}
      />

      <SelectorModal
        title="Wybierz klienta"
        isOpen={showSelectClientModal}
        onClose={() => setShowSelectClientModal(false)}
        values={clientOptions}
        onSelect={option => handleSelectClient(option)}
        selectedIDs={[report.clientId]}
      />
    </div>
  );
}

AttendanceReport.propTypes = {
  userId: PropTypes.string.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  schoolClasses: PropTypes.array.isRequired,
  loadSchoolClasses: PropTypes.func.isRequired,
  teachers: PropTypes.array.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  students: PropTypes.array.isRequired,
  loadStudents: PropTypes.func.isRequired,
  classStudents: PropTypes.array.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  clients: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  clientOptions: PropTypes.array.isRequired,
  teacherOptions: PropTypes.array.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
};

AttendanceReport.defaultProps = {
  userRole: "",
};

const mapStateToProps = state => ({
  lessonRegister: state.lessonRegister,
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  clients: state.clients,
  clientOptions: [
    { value: "all", text: "Wszyscy" },
    ...(state.clients || []).filter(c => !c.deleted).map(c => ({
      value: c.id,
      text: `${c.firstName} ${c.lastName}`,
    })),
  ],
  teacherOptions: [
    { value: "all", text: "Wszyscy" },
    ...(state.teachers || []).map(t => ({
      value: t.id,
      text: `${t.firstName} ${t.lastName}`,
    })),
  ],
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

const mapDispatchToProps = {
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadClients: clientActions.loadClients,
};

export default connect(mapStateToProps, mapDispatchToProps)(AttendanceReport);
